// Persistance locale des récitations : erreurs par verset et historique des
// examens. Tout reste dans le navigateur — aucune donnée de récitation ne part
// vers un serveur, ce qui est cohérent avec le reste de l'application.

const STORAGE_KEY = 'quran-exam:history:v1';

// Bornes : l'historique ne doit jamais faire échouer une écriture pour cause de
// quota dépassé. On garde les examens récents et les versets les plus travaillés.
const MAX_EXAMS = 50;
const MAX_VERSES = 500;

// Fabrique une structure NEUVE à chaque appel. Un objet constant partagé serait
// muté par les enregistrements successifs : les compteurs s'accumuleraient
// d'une session à l'autre même après effacement.
const emptyHistory = () => ({ version: 1, exams: [], verses: {} });

function safeParse(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      version: 1,
      exams: Array.isArray(parsed.exams) ? parsed.exams : [],
      verses: parsed.verses && typeof parsed.verses === 'object' ? parsed.verses : {},
    };
  } catch (error) {
    // Donnée corrompue ou format d'une version antérieure : on repart à vide
    // plutôt que de faire planter l'application au démarrage.
    return null;
  }
}

export function loadHistory() {
  try {
    return safeParse(window.localStorage.getItem(STORAGE_KEY)) || emptyHistory();
  } catch (error) {
    // Navigation privée, stockage désactivé : l'application doit continuer.
    return emptyHistory();
  }
}

function save(history) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    // Quota atteint ou écriture refusée : on renonce silencieusement, la
    // persistance est un confort et ne doit jamais bloquer la récitation.
  }
  return history;
}

export function clearHistory() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    /* rien à faire */
  }
  return emptyHistory();
}

export const verseKey = (surah, ayah) => `${surah}:${ayah}`;

/**
 * Enregistre une tentative sur un verset.
 * Les compteurs sont cumulés pour faire ressortir les versets qui résistent.
 */
export function recordVerseAttempt({ surah, ayah, correct = 0, errors = 0, at }) {
  if (!surah || !ayah) return loadHistory();
  const history = loadHistory();
  const key = verseKey(surah, ayah);
  const previous = history.verses[key] || {
    surah, ayah, attempts: 0, correct: 0, errors: 0, lastAt: null,
  };
  const evaluated = Number(correct) + Number(errors);

  history.verses[key] = {
    surah,
    ayah,
    attempts: previous.attempts + 1,
    correct: previous.correct + Number(correct),
    errors: previous.errors + Number(errors),
    lastAccuracy: evaluated ? Number(correct) / evaluated : 0,
    lastAt: at || new Date().toISOString(),
  };

  const keys = Object.keys(history.verses);
  if (keys.length > MAX_VERSES) {
    // On sacrifie les versets les plus anciens, pas les plus fautifs.
    keys
      .sort((a, b) => String(history.verses[a].lastAt).localeCompare(String(history.verses[b].lastAt)))
      .slice(0, keys.length - MAX_VERSES)
      .forEach((stale) => delete history.verses[stale]);
  }
  return save(history);
}

export function startExam({ id, scope, questionCount, versesTarget, at }) {
  const history = loadHistory();
  history.exams = [
    {
      id,
      scope: scope || '',
      questionCount: Number(questionCount) || 0,
      versesTarget: Number(versesTarget) || 0,
      passed: 0,
      toReview: 0,
      startedAt: at || new Date().toISOString(),
      updatedAt: at || new Date().toISOString(),
    },
    ...history.exams,
  ].slice(0, MAX_EXAMS);
  return save(history);
}

export function recordQuestionResult(examId, result, at) {
  const history = loadHistory();
  const exam = history.exams.find((item) => item.id === examId);
  if (!exam) return history;
  if (result === 'passed') exam.passed += 1;
  else exam.toReview += 1;
  exam.updatedAt = at || new Date().toISOString();
  return save(history);
}

/** Versets classés par nombre d'erreurs décroissant : là où réviser en priorité. */
export function rankVersesByErrors(history, limit = 10) {
  return Object.values(history.verses || {})
    .filter((verse) => verse.errors > 0)
    .sort((a, b) => b.errors - a.errors || b.attempts - a.attempts)
    .slice(0, limit);
}

export function historyTotals(history) {
  const verses = Object.values(history.verses || {});
  const correct = verses.reduce((total, verse) => total + verse.correct, 0);
  const errors = verses.reduce((total, verse) => total + verse.errors, 0);
  const attempts = verses.reduce((total, verse) => total + verse.attempts, 0);
  return {
    distinctVerses: verses.length,
    attempts,
    correct,
    errors,
    accuracy: correct + errors ? correct / (correct + errors) : 0,
  };
}
